import React, {useEffect, useRef, useState} from 'react';
import { Card, Container, Row, Col, Button, ButtonGroup, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faClock, faMapMarkerAlt, faUsers, faTag, faCalendarAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import {useDevice} from "@/contexts/DeviceContext.jsx";
import {useAuth} from "@/contexts/AuthContext.jsx";
import {supabaseDataService} from "@/services/supabaseService.js";
import moment from "moment";
import _ from "lodash";

const Calendar = ({ user }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const initialLoadRef = useRef(true);
    const [error, setError] = useState('');
    const { isMobile, isTablet, isDesktop, windowWidth } = useDevice();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentView, setCurrentView] = useState('day'); // 'day', 'week', 'month'
    const [firstHour, setFirstHour] = useState(0);
    const [timeSlots, setTimeSlots] = useState([
        '12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
        '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM'
    ]);
    const [timeSlotsHrs, setTimeSlotsHrs] = useState([
        0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23
    ]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const datePickerRef = useRef(null);
    const scrollAreaRef = useRef(null);
    const [showEventDialog, setShowEventDialog] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [pickerCurrentMonth, setPickerCurrentMonth] = useState(new Date());

    useEffect(() => {
        // Only fetch events once when the component mounts
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            fetchEvents();
        }
    }, []);

    // Fetch events when view changes
    useEffect(() => {
        if (!initialLoadRef.current) {
            fetchEvents();
        }
    }, [currentView, selectedDate]);

    // Scroll to current time when day view finishes loading
    useEffect(() => {
        if (!loading && currentView === 'day' && scrollAreaRef.current) {
            const currentHour = new Date().getHours();
            const rowHeight = 53;
            const scrollTo = Math.max(0, (currentHour - 1) * rowHeight);
            scrollAreaRef.current.scrollTop = scrollTo;
        }
    }, [loading, currentView]);

    // Handle clicking outside datepicker to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        };

        if (showDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showDatePicker]);

    const getDateRange = (date = selectedDate, view = currentView) => {
        const momentDate = moment(date);

        switch (view) {
            case 'week':
                const startOfWeek = momentDate.clone().startOf('week');
                const endOfWeek = momentDate.clone().endOf('week');
                return { start: startOfWeek, end: endOfWeek };
            case 'month':
                const startOfMonth = momentDate.clone().startOf('month').startOf('week');
                const endOfMonth = momentDate.clone().endOf('month').endOf('week');
                return { start: startOfMonth, end: endOfMonth };
            default: // day
                const startOfDay = momentDate.clone().startOf('day');
                const endOfDay = momentDate.clone().endOf('day');
                return { start: startOfDay, end: endOfDay };
        }
    };

    const addTimeSlots = (hour, currentSlots) => {
        const existingSlots = currentSlots || [...timeSlotsHrs];
        const min = _.min(existingSlots);
        const max = _.max(existingSlots);

        let newSlots = [...existingSlots];

        if (hour < min) {
            for (let i = hour; i < min; i++) {
                newSlots.push(i);
            }
        }

        if (hour > max) {
            for (let i = max + 1; i <= hour; i++) {
                newSlots.push(i);
            }
        }

        return _.sortBy(_.uniq(newSlots));
    };

    const assignEventColumns = (events, date = null) => {
        const sortedEvents = _.sortBy(events, [
            event => event.startMoment.valueOf()
        ]);

        const timeSlotGroups = {};

        sortedEvents.forEach(event => {
            if (event.isAllday) {
                event.column = -1;
                return;
            }

            // For week view, we need to consider the day as well
            const eventDate = event.startMoment.format('YYYY-MM-DD');
            const dayOffset = currentView === 'week' && date ?
                moment(eventDate).diff(moment(date).startOf('week'), 'days') : 0;

            const startDecimalHour = parseFloat(event.startMoment.format('HH')) +
                parseFloat(event.startMoment.format('mm')) / 60;
            const endDecimalHour = parseFloat(event.endMoment.format('HH')) +
                parseFloat(event.endMoment.format('mm')) / 60;

            const startHr = Math.floor(startDecimalHour);
            const endHr = Math.ceil(endDecimalHour);

            let columnAssigned = false;
            let columnToUse = 0;

            while (!columnAssigned) {
                let canUseColumn = true;

                for (let hour = startHr; hour < endHr; hour++) {
                    const key = `${dayOffset}_${hour}_${columnToUse}`;
                    if (timeSlotGroups[key]) {
                        canUseColumn = false;
                        break;
                    }
                }

                if (canUseColumn) {
                    for (let hour = startHr; hour < endHr; hour++) {
                        const key = `${dayOffset}_${hour}_${columnToUse}`;
                        timeSlotGroups[key] = event.event_id;
                    }

                    event.column = columnToUse;
                    event.dayOffset = dayOffset;
                    columnAssigned = true;
                } else {
                    columnToUse++;
                }
            }

            event.startDecimalHour = startDecimalHour;
            event.endDecimalHour = endDecimalHour;
        });

        return sortedEvents;
    };

    const fetchEvents = async (date = selectedDate, view = currentView) => {
        try {
            let updatedTimeSlots = [...timeSlotsHrs];
            setLoading(true);

            const { start, end } = getDateRange(date, view);
            const eventPromises = [];

            // Fetch events for date range
            if (view === 'day') {
                eventPromises.push(supabaseDataService.getEvents(user.id, date));
            } else {
                // For week and month views, fetch events for each day in range
                const current = start.clone();
                while (current.isSameOrBefore(end, 'day')) {
                    eventPromises.push(supabaseDataService.getEvents(user.id, current.toDate()));
                    current.add(1, 'day');
                }
            }

            const eventArrays = await Promise.all(eventPromises);
            const allEvents = _.flatten(eventArrays);

            // Process all events
            allEvents.forEach(event => {
                const stStr = _.get(event, 'start_date') + ' ' + (_.get(event, 'start_time') || '00:00:00');
                const st = moment(stStr);

                if (!st.isValid()) {
                    return;
                }
                event.startMoment = st;

                event.isAllday = !_.get(event, 'start_time');
                if (!event.isAllday) {
                    const hour = st.hour();
                    updatedTimeSlots = addTimeSlots(hour, updatedTimeSlots);
                }

                const etStr = _.get(event, 'end_date') + ' ' + (_.get(event, 'end_time') || '00:00:00');
                let et = moment(etStr);

                if (!et.isValid()) {
                    et = st.clone().add(1, 'hours');
                }

                if (!event.isAllday && (_.get(event, 'end_time') == null || et.format('HH:mm:ss') == '00:00:00')) {
                    et = moment(st);
                    et.add(1, 'hours');
                    event.endMoment = moment(et.format('YYYY-MM-DD HH:mm:ss'));
                    event.end_time = event.endMoment.format('HH:mm:ss');
                } else {
                    event.endMoment = et;
                }

                if (!event.isAllday) {
                    const endHour = et.hour();
                    updatedTimeSlots = addTimeSlots(endHour, updatedTimeSlots);
                }
            });

            const eventsWithColumns = assignEventColumns(allEvents, date);

            setTimeSlotsHrs(updatedTimeSlots);
            setEvents(eventsWithColumns);
            setError('');
        } catch (err) {
            setError('Failed to load Events. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatDateHeader = (date) => {
        switch (currentView) {
            case 'week':
                const weekStart = moment(date).startOf('week');
                const weekEnd = moment(date).endOf('week');
                return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
            case 'month':
                return moment(date).format('MMMM YYYY');
            default:
                return formatDate(date);
        }
    };

    const formatTimeSlow = (hour) => {
        if (hour === 0) {
            return '12 AM';
        } else if (hour < 12) {
            return hour + ' AM';
        } else if (hour === 12) {
            return '12 PM';
        } else {
            return hour - 12 + ' PM';
        }
    };

    const getEventColor = (event) => {
        const colorMap = {
            'Extracurriculars': 'rgba(64, 159, 255, 0.7)',
            'Work': 'rgba(100, 223, 223, 0.7)',
            'School': 'rgba(251, 177, 60, 0.7)',
            'Personal': 'rgba(144, 238, 144, 0.7)',
            'Family': 'rgba(255, 182, 193, 0.7)',
        };

        return colorMap[event.category] || 'rgba(224, 248, 255, 0.7)';
    };

    const arrowClick = (direction) => {
        let newDate;
        if (direction === 'down') {
            switch (currentView) {
                case 'week':
                    newDate = moment(selectedDate).subtract(1, 'weeks');
                    break;
                case 'month':
                    newDate = moment(selectedDate).subtract(1, 'months');
                    break;
                default:
                    newDate = moment(selectedDate).subtract(1, 'days');
                    break;
            }
        } else {
            switch (currentView) {
                case 'week':
                    newDate = moment(selectedDate).add(1, 'weeks');
                    break;
                case 'month':
                    newDate = moment(selectedDate).add(1, 'months');
                    break;
                default:
                    newDate = moment(selectedDate).add(1, 'days');
                    break;
            }
        }

        setSelectedDate(newDate.toDate());
    };

    // Calendar Popup Component
    const CalendarPopup = () => {
        const monthStart = moment(pickerCurrentMonth).startOf('month');
        const calendarStart = monthStart.clone().startOf('week');
        const monthEnd = moment(pickerCurrentMonth).endOf('month');
        const calendarEnd = monthEnd.clone().endOf('week');

        const weeks = [];
        const current = calendarStart.clone();

        while (current.isSameOrBefore(calendarEnd, 'day')) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                week.push(current.clone());
                current.add(1, 'day');
            }
            weeks.push(week);
        }

        const handleDateSelect = (date) => {
            setSelectedDate(date.toDate());
            setShowDatePicker(false);
        };

        const navigateMonth = (direction) => {
            const newMonth = moment(pickerCurrentMonth).add(direction, 'month');
            setPickerCurrentMonth(newMonth.toDate());
        };

        return (
            <div
                className="position-absolute bg-white border rounded shadow-lg"
                style={{
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    minWidth: '280px',
                    marginTop: '5px'
                }}>

                {/* Month Header */}
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                    <Button
                        variant="link"
                        className="text-secondary p-0"
                        onClick={() => navigateMonth(-1)}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </Button>

                    <h6 className="m-0 fw-bold">
                        {moment(pickerCurrentMonth).format('MMMM YYYY')}
                    </h6>

                    <Button
                        variant="link"
                        className="text-secondary p-0"
                        onClick={() => navigateMonth(1)}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Button>
                </div>

                {/* Day Headers */}
                <div className="d-flex px-2 py-1 bg-light">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} className="flex-fill text-center py-1">
                            <small className="text-muted fw-bold">{day}</small>
                        </div>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="p-2">
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="d-flex">
                            {week.map((day, dayIndex) => {
                                const isCurrentMonth = day.month() === moment(pickerCurrentMonth).month();
                                const isToday = day.isSame(moment(), 'day');
                                const isSelected = day.isSame(selectedDate, 'day');

                                return (
                                    <div
                                        key={dayIndex}
                                        className="flex-fill text-center p-1"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleDateSelect(day)}>
                                        <div
                                            className={`rounded-circle d-flex align-items-center justify-content-center`}
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                margin: '0 auto',
                                                fontSize: '14px',
                                                fontWeight: isToday || isSelected ? '600' : '400',
                                                color: isCurrentMonth ?
                                                    (isSelected ? 'white' :
                                                        isToday ? '#2B7D87' : '#333') : '#ccc',
                                                backgroundColor: isSelected ? '#2B7D87' :
                                                    (isToday ? 'rgba(43, 125, 135, 0.1)' : 'transparent'),
                                                border: isToday && !isSelected ? '1px solid #2B7D87' : 'none',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) {
                                                    e.target.style.backgroundColor = 'rgba(43, 125, 135, 0.1)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected && !isToday) {
                                                    e.target.style.backgroundColor = 'transparent';
                                                } else if (isToday && !isSelected) {
                                                    e.target.style.backgroundColor = 'rgba(43, 125, 135, 0.1)';
                                                }
                                            }}>
                                            {day.format('D')}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Today Button */}
                <div className="p-2 border-top">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="w-100"
                        onClick={() => handleDateSelect(moment())}>
                        Today
                    </Button>
                </div>
            </div>
        );
    };

    // Event Detail Dialog Component
    const EventDetailDialog = () => {
        if (!selectedEvent) return null;

        const formatEventTime = (event) => {
            if (event.isAllday) {
                return 'All day';
            }

            const startTime = event.startMoment.format('h:mm A');
            const endTime = event.endMoment.format('h:mm A');
            return `${startTime} - ${endTime}`;
        };

        const formatEventDate = (event) => {
            const startDate = moment(event.start_date).format('dddd, MMMM D, YYYY');

            if (event.end_date && event.end_date !== event.start_date) {
                const endDate = moment(event.end_date).format('dddd, MMMM D, YYYY');
                return `${startDate} - ${endDate}`;
            }

            return startDate;
        };

        const getCategoryColor = (category) => {
            const colorMap = {
                'Extracurriculars': '#409FFF',
                'Work': '#64DFDF',
                'School': '#FBB13C',
                'Personal': '#90EE90',
                'Family': '#FFB6C1',
            };
            return colorMap[category] || '#E0F8FF';
        };

        return (
            <Modal
                show={showEventDialog}
                onHide={closeEventDialog}
                size="md"
                centered>
                <Modal.Header className="border-0 pb-0">
                    <Modal.Title className="d-flex align-items-center w-100">
                        <div className="flex-grow-1">
                            <h4 className="mb-0" style={{ color: '#2B7D87', fontWeight: '600' }}>
                                {selectedEvent.event_name || selectedEvent.description || 'Event Details'}
                            </h4>
                        </div>
                        <Button
                            variant="link"
                            className="p-0 text-secondary"
                            onClick={closeEventDialog}>
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                        </Button>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-2">
                    <div className="mb-4">
                        {/* Date and Time */}
                        <div className="d-flex align-items-center mb-3">
                            <FontAwesomeIcon
                                icon={faCalendarAlt}
                                className="me-3 text-secondary"
                                style={{ width: '20px' }}
                            />
                            <div>
                                <div style={{ fontWeight: '500', fontSize: '16px' }}>
                                    {formatEventDate(selectedEvent)}
                                </div>
                                <div className="text-secondary" style={{ fontSize: '14px' }}>
                                    {formatEventTime(selectedEvent)}
                                </div>
                            </div>
                        </div>

                        {/* Location */}
                        {selectedEvent.location && (
                            <div className="d-flex align-items-center mb-3">
                                <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    className="me-3 text-secondary"
                                    style={{ width: '20px' }}
                                />
                                <div style={{ fontSize: '15px' }}>
                                    {selectedEvent.location}
                                </div>
                            </div>
                        )}

                        {/* Category */}
                        {selectedEvent.category && (
                            <div className="d-flex align-items-center mb-3">
                                <FontAwesomeIcon
                                    icon={faTag}
                                    className="me-3 text-secondary"
                                    style={{ width: '20px' }}
                                />
                                <div>
                                    <span
                                        className="badge px-3 py-2"
                                        style={{
                                            backgroundColor: getCategoryColor(selectedEvent.category),
                                            color: '#333',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}>
                                        {selectedEvent.category}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Description/Notes */}
                        {selectedEvent.notes && (
                            <div className="mt-4">
                                <h6 style={{ color: '#6c757d', fontWeight: '600', marginBottom: '8px' }}>
                                    Notes
                                </h6>
                                <div
                                    className="p-3 rounded"
                                    style={{
                                        backgroundColor: '#f8f9fa',
                                        fontSize: '14px',
                                        lineHeight: '1.5'
                                    }}>
                                    {selectedEvent.notes}
                                </div>
                            </div>
                        )}

                        {/* Additional Details */}
                        {(selectedEvent.event_id || selectedEvent.created_at) && (
                            <div className="mt-4 pt-3 border-top">
                                <small className="text-muted">
                                    {selectedEvent.created_at && (
                                        <div>Created: {moment(selectedEvent.created_at).format('MMM D, YYYY')}</div>
                                    )}
                                </small>
                            </div>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 pt-0">
                    <Button
                        variant="secondary"
                        onClick={closeEventDialog}
                        style={{ minWidth: '80px' }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const handleDatePickerChange = (event) => {
        const newDate = new Date(event.target.value);
        setSelectedDate(newDate);
        setShowDatePicker(false);
    };

    const toggleDatePicker = () => {
        setShowDatePicker(!showDatePicker);
        setPickerCurrentMonth(selectedDate); // Set picker to show current selected month
    };

    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setShowEventDialog(true);
    };

    const closeEventDialog = () => {
        setShowEventDialog(false);
        setSelectedEvent(null);
    };

    // Day View Component
    const DayView = () => {
        const isEventVisible = (event, timeSlot) => {
            if (event.isAllday) {
                return timeSlot === timeSlotsHrs[0];
            }

            const startHr = Math.floor(event.startDecimalHour);
            const endHr = Math.ceil(event.endDecimalHour);
            return timeSlot >= startHr && timeSlot < endHr;
        };

        const isFirstOccurrence = (event, timeSlot) => {
            return Math.floor(event.startDecimalHour) === timeSlot;
        };

        const calculateEventHeight = (event) => {
            if (event.isAllday) return 53;
            const durationHours = event.endDecimalHour - event.startDecimalHour;
            return durationHours * 53;
        };

        const calculateEventTop = (event, timeSlot) => {
            if (event.isAllday) return 0;
            const minutesOffset = (event.startDecimalHour - Math.floor(event.startDecimalHour)) * 60;
            return (minutesOffset / 60) * 53;
        };

        const getMaxColumns = () => {
            const columns = events
                .filter(event => event.column !== -1)
                .map(event => event.column);
            const maxColumn = columns.length > 0 ? Math.max(...columns) : 0;
            return maxColumn + 1;
        };

        const EventItem = ({ event, timeSlot }) => {
            if (!isEventVisible(event, timeSlot)) return null;
            if (!isFirstOccurrence(event, timeSlot) && !event.isAllday) return null;

            const height = calculateEventHeight(event);
            const topOffset = calculateEventTop(event, timeSlot);
            const maxColumns = getMaxColumns();

            const eventStyle = {
                backgroundColor: getEventColor(event),
                borderRadius: '5px',
                padding: '8px',
                height: `${height}px`,
                position: 'absolute',
                top: `${topOffset}px`,
                zIndex: 10,
                overflow: 'hidden',
                fontSize: '11px',
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            };

            if (event.isAllday || event.column === -1) {
                eventStyle.width = 'calc(100% - 16px)';
                eventStyle.left = '8px';
                eventStyle.backgroundColor = 'rgba(238, 238, 255, 0.7)';
            } else {
                const columnWidth = 100 / maxColumns;
                eventStyle.width = `calc(${columnWidth}% - 16px)`;
                eventStyle.left = `calc(${event.column * columnWidth}% + 8px)`;
            }

            return (
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleEventClick(event);
                        }
                    }}
                    style={{
                        ...eventStyle,
                        cursor: 'pointer',
                        transition: 'transform 0.1s, box-shadow 0.1s'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{ fontWeight: 'bold' }}>{event.event_name || event.description}</div>
                    {event.location && <div style={{ fontSize: '10px' }}>{event.location}</div>}
                    {event.people && <div style={{ fontSize: '10px' }}>{event.people}</div>}
                </div>
            );
        };

        const todaysEvents = events.filter(event =>
            moment(event.start_date).isSame(selectedDate, 'day')
        );

        return (
            <div className="px-3">
                {/* All-day events section */}
                <div className="mb-2">
                    {todaysEvents
                        .filter(event => event.isAllday)
                        .map((event, index) => (
                            <div
                                key={`allday-${index}`}
                                className="mb-1 p-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEventClick(event);
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleEventClick(event);
                                    }
                                }}
                                style={{
                                    backgroundColor: 'rgba(238, 238, 255, 0.7)',
                                    borderRadius: '5px',
                                    fontSize: '11px',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    cursor: 'pointer',
                                    transition: 'transform 0.1s, box-shadow 0.1s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = 'none';
                                }}>
                                <span style={{ fontWeight: 'bold' }}>{event.event_name || event.description}</span>
                                {event.location && <span style={{ fontSize: '10px' }}> • {event.location}</span>}
                            </div>
                        ))
                    }
                </div>

                {/* Time-based events */}
                {timeSlotsHrs.map((timeSlot, index) => (
                    <Row key={index} className="border-top border-light g-0" style={{ position: 'relative', height: '53px' }}>
                        <Col xs={2} className="py-2 text-end pe-2" style={{ color: '#6c757d' }}>
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>{formatTimeSlow(timeSlot)}</span>
                        </Col>

                        <Col xs={10} className="position-relative">
                            {todaysEvents
                                .filter(event => !event.isAllday)
                                .map((event, eventIndex) => (
                                    <EventItem key={eventIndex} event={event} timeSlot={timeSlot} />
                                ))
                            }

                            <div className="d-flex h-100">
                                <div className="flex-grow-1 border-bottom border-light"></div>
                            </div>
                        </Col>
                    </Row>
                ))}
            </div>
        );
    };

    // Week View Component
    const WeekView = () => {
        const weekStart = moment(selectedDate).startOf('week');
        const weekDays = [];

        for (let i = 0; i < 7; i++) {
            weekDays.push(weekStart.clone().add(i, 'days'));
        }

        const getEventsForDay = (day) => {
            return events.filter(event =>
                moment(event.start_date).isSame(day, 'day')
            );
        };

        const EventItem = ({ event, timeSlot, dayIndex }) => {
            if (event.isAllday) return null;

            const startHr = Math.floor(event.startDecimalHour);
            const endHr = Math.ceil(event.endDecimalHour);

            if (timeSlot < startHr || timeSlot >= endHr) return null;
            if (Math.floor(event.startDecimalHour) !== timeSlot) return null;

            const height = (event.endDecimalHour - event.startDecimalHour) * 53;
            const topOffset = ((event.startDecimalHour - Math.floor(event.startDecimalHour)) * 60 / 60) * 53;

            const eventStyle = {
                backgroundColor: getEventColor(event),
                borderRadius: '3px',
                padding: '4px',
                height: `${height}px`,
                position: 'absolute',
                top: `${topOffset}px`,
                left: '2px',
                right: '2px',
                zIndex: 10,
                overflow: 'hidden',
                fontSize: '10px',
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            };

            return (
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleEventClick(event);
                        }
                    }}
                    style={{
                        ...eventStyle,
                        cursor: 'pointer',
                        transition: 'transform 0.1s, box-shadow 0.1s'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{ fontWeight: 'bold', fontSize: '9px' }}>
                        {event.event_name || event.description}
                    </div>
                    {event.location && <div style={{ fontSize: '8px' }}>{event.location}</div>}
                </div>
            );
        };

        return (
            <div className="flex-grow-1 overflow-auto">
                {/* Week header with day names and dates */}
                <div className="d-flex border-bottom border-light sticky-top bg-white">
                    <div style={{ width: '80px' }}></div>
                    {weekDays.map((day, index) => (
                        <div key={index} className="flex-fill text-center py-2 border-start border-light">
                            <div style={{ fontSize: '12px', fontWeight: '500', color: '#6c757d' }}>
                                {day.format('ddd')}
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '600',
                                color: day.isSame(moment(), 'day') ? '#2B7D87' : '#333' }}>
                                {day.format('D')}
                            </div>
                            {/* All-day events for this day */}
                            {getEventsForDay(day).filter(event => event.isAllday).map((event, eventIndex) => (
                                <div
                                    key={eventIndex}
                                    className="mt-1 mx-1 p-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEventClick(event);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleEventClick(event);
                                        }
                                    }}
                                    style={{
                                        backgroundColor: 'rgba(238, 238, 255, 0.7)',
                                        borderRadius: '3px',
                                        fontSize: '9px',
                                        border: '1px solid rgba(0,0,0,0.1)',
                                        cursor: 'pointer',
                                        transition: 'transform 0.1s, box-shadow 0.1s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'scale(1.05)';
                                        e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'scale(1)';
                                        e.target.style.boxShadow = 'none';
                                    }}>
                                    {event.event_name || event.description}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                {/* Time slots with events */}
                {timeSlotsHrs.map((timeSlot, timeIndex) => (
                    <div key={timeIndex} className="d-flex border-bottom border-light" style={{ height: '53px', position: 'relative' }}>
                        <div style={{ width: '80px' }} className="py-2 text-end pe-2 border-end border-light">
                            <span style={{ fontWeight: '500', fontSize: '12px', color: '#6c757d' }}>
                                {formatTimeSlow(timeSlot)}
                            </span>
                        </div>
                        {weekDays.map((day, dayIndex) => (
                            <div key={dayIndex} className="flex-fill border-start border-light position-relative">
                                {getEventsForDay(day)
                                    .filter(event => !event.isAllday)
                                    .map((event, eventIndex) => (
                                        <EventItem
                                            key={eventIndex}
                                            event={event}
                                            timeSlot={timeSlot}
                                            dayIndex={dayIndex}
                                        />
                                    ))
                                }
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    // Month View Component
    const MonthView = () => {
        const monthStart = moment(selectedDate).startOf('month');
        const calendarStart = monthStart.clone().startOf('week');
        const monthEnd = moment(selectedDate).endOf('month');
        const calendarEnd = monthEnd.clone().endOf('week');

        const weeks = [];
        const current = calendarStart.clone();

        while (current.isSameOrBefore(calendarEnd, 'day')) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                week.push(current.clone());
                current.add(1, 'day');
            }
            weeks.push(week);
        }

        const getEventsForDay = (day) => {
            return events.filter(event =>
                moment(event.start_date).isSame(day, 'day')
            );
        };

        return (
            <div className="flex-grow-1 overflow-auto p-3">
                {/* Day headers */}
                <div className="d-flex mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="flex-fill text-center py-2">
                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#6c757d' }}>
                                {day}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Calendar grid */}
                {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="d-flex" style={{ height: '120px' }}>
                        {week.map((day, dayIndex) => {
                            const dayEvents = getEventsForDay(day);
                            const isCurrentMonth = day.month() === moment(selectedDate).month();
                            const isToday = day.isSame(moment(), 'day');
                            const isSelected = day.isSame(selectedDate, 'day');

                            return (
                                <div key={dayIndex}
                                     className="flex-fill border border-light p-1 position-relative"
                                     style={{
                                         backgroundColor: isSelected ? 'rgba(43, 125, 135, 0.1)' : 'transparent',
                                         cursor: 'pointer'
                                     }}
                                     onClick={() => setSelectedDate(day.toDate())}>
                                    <div style={{
                                        fontSize: '14px',
                                        fontWeight: isToday ? '700' : '500',
                                        color: isCurrentMonth ? (isToday ? '#2B7D87' : '#333') : '#ccc'
                                    }}>
                                        {day.format('D')}
                                    </div>

                                    {/* Events for this day */}
                                    <div className="mt-1">
                                        {dayEvents.slice(0, 3).map((event, eventIndex) => (
                                            <div
                                                key={eventIndex}
                                                className="mb-1 px-1 py-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEventClick(event);
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleEventClick(event);
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: getEventColor(event),
                                                    borderRadius: '2px',
                                                    fontSize: '9px',
                                                    fontWeight: '500',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    border: '1px solid rgba(0,0,0,0.1)',
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.1s, box-shadow 0.1s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.transform = 'scale(1.02)';
                                                    e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.transform = 'scale(1)';
                                                    e.target.style.boxShadow = 'none';
                                                }}>
                                                {event.event_name || event.description}
                                            </div>
                                        ))}
                                        {dayEvents.length > 3 && (
                                            <div style={{ fontSize: '8px', color: '#6c757d', fontWeight: '500' }}>
                                                +{dayEvents.length - 3} more
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    const renderView = () => {
        switch (currentView) {
            case 'week':
                return <WeekView />;
            case 'month':
                return <MonthView />;
            default:
                return <DayView />;
        }
    };

    return (
        <Card className="calendar-card dashboard-right-card">
            <Card.Body className="d-flex flex-column p-0">
                {/* EVENTS Header */}
                <div className="text-center pt-4 pb-2">
                    <p style={{
                        color: '#07BCD9',
                        fontSize: '12px',
                        fontWeight: '600',
                        letterSpacing: '3px',
                        margin: 0
                    }}>EVENTS</p>
                </div>

                {/* View toggle */}
                <div className="d-flex justify-content-center gap-1 pb-2">
                    {['day', 'week', 'month'].map(view => (
                        <button
                            key={view}
                            onClick={() => setCurrentView(view)}
                            style={{
                                background: currentView === view ? '#07BCD9' : 'transparent',
                                color: currentView === view ? '#fff' : '#07BCD9',
                                border: '1.5px solid #07BCD9',
                                borderRadius: '50px',
                                padding: '2px 14px',
                                fontSize: '11px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                textTransform: 'capitalize'
                            }}>
                            {view}
                        </button>
                    ))}
                </div>

                {/* Date navigation row */}
                <div className="d-flex align-items-center justify-content-between px-4 py-2">
                    <Button
                        variant="link"
                        className="p-0"
                        style={{ color: '#aaa' }}
                        onClick={() => arrowClick('down')}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </Button>

                    <div className="position-relative" ref={datePickerRef}>
                        <span
                            style={{
                                fontSize: '14px',
                                color: '#575757',
                                fontWeight: '300',
                                cursor: 'pointer'
                            }}
                            onClick={toggleDatePicker}>
                            {formatDateHeader(selectedDate)}
                        </span>

                        {showDatePicker && <CalendarPopup />}
                    </div>

                    <Button
                        variant="link"
                        className="p-0"
                        style={{ color: '#aaa' }}
                        onClick={() => arrowClick('up')}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Button>
                </div>

                <div className="calendar-scroll-area" ref={scrollAreaRef}>
                    {loading && (
                        <div className="text-center p-4">
                            <span>Loading events...</span>
                        </div>
                    )}

                    {error && (
                        <div className="text-center p-4 text-danger">
                            <span>{error}</span>
                        </div>
                    )}

                    {!loading && !error && renderView()}
                </div>

                {/* Event Detail Dialog */}
                <EventDetailDialog />
            </Card.Body>
        </Card>
    );
};

export default Calendar;